var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import Box from '../../Box/BoxBase';
import getUniqueKey from '../../utils/customKey';
import { SkeletonRow } from '../Rows';
import TablePagination from '../TablePagination';
import Typography from '../../Typography';
import { HeaderStyled, HeaderDataContainerStyled, EmptyTableRowStyled, TableContainerStyled, TableDataContainerStyled, TableContentContainerStyled, EmptyTableRowsContainerStyled, NoResultsFoundContainerStyled, } from './styled';
const ComplexTable = (_a) => {
    var props = __rest(_a, []);
    const { data, emptyLabel, labels, total, rowsPerPage, page, notifyParent, RowController, fetchOnPageChange, rowControllerProps, rowsPerPageOptions, loading, style: tableStyleProp } = props, rest = __rest(props, ["data", "emptyLabel", "labels", "total", "rowsPerPage", "page", "notifyParent", "RowController", "fetchOnPageChange", "rowControllerProps", "rowsPerPageOptions", "loading", "style"]);
    const [rowItems, setRowItems] = useState({});
    const [loadingData, setLoadingData] = useState(loading);
    const [rowHeight, setRowHeight] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
    const tableRef = useRef(null);
    const tableContentRef = useRef(null);
    const tableHeaderRef = useRef(null);
    const minWidth = 75;
    const rowsPerPageCurrentOptions = (rowsPerPageOptions === null || rowsPerPageOptions === void 0 ? void 0 : rowsPerPageOptions.length) > 0 ? rowsPerPageOptions : [rowsPerPage];
    const getDefaultColumnWidth = useCallback(() => {
        var _a;
        const rawTableWidth = ((_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
        const totalFixedWidth = labels.reduce((sum, { width }) => sum + (width || 0), 0);
        const flexibleColumnsCount = labels.filter(({ width }) => !width).length;
        const tableWidth = rawTableWidth < totalFixedWidth
            ? totalFixedWidth + minWidth * flexibleColumnsCount
            : rawTableWidth;
        const remainingWidth = tableWidth - totalFixedWidth;
        if (flexibleColumnsCount === 0)
            return minWidth;
        const defaultWidth = remainingWidth / flexibleColumnsCount;
        return defaultWidth > minWidth - 24 ? defaultWidth : minWidth;
    }, [labels]);
    const getSliceToShow = () => {
        const pageLimitIndicator = rowsPerPage > total ? total : rowsPerPage;
        if (fetchOnPageChange)
            return [0, pageLimitIndicator];
        return [page * pageLimitIndicator, page * pageLimitIndicator + pageLimitIndicator];
    };
    const getEmptyRows = () => {
        const dataLimit = Math.min(data.length, total);
        if (fetchOnPageChange)
            return Math.max(0, rowsPerPage - dataLimit);
        return Math.max(0, Math.min(rowsPerPage, (1 + page) * rowsPerPage - dataLimit));
    };
    const handlePageChange = (__event, newPage) => {
        notifyParent({ type: 'changePage', data: { newPage, rowsPerPage } });
    };
    const handleRowsPerPageChange = (__event, newRowsPerPage) => {
        notifyParent({ type: 'changeRowsPerPage', data: { newRowsPerPage } });
    };
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 700);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    useEffect(() => {
        const defaultColumnWidth = getDefaultColumnWidth();
        setRowItems(labels.reduce((previous, { value: currentValue, width }) => {
            return Object.assign(Object.assign({}, previous), { [currentValue]: { value: currentValue, width: width || defaultColumnWidth } });
        }, {}));
    }, [labels]);
    useEffect(() => {
        if (loading)
            setLoadingData(true);
    }, [loading]);
    useEffect(() => {
        if (!loading)
            setLoadingData(false);
    }, [data, loading]);
    useEffect(() => {
        if (tableContentRef.current && tableHeaderRef.current) {
            const contentHeight = tableContentRef.current.clientHeight || 0;
            const headerHeight = tableHeaderRef.current.clientHeight || 0;
            setRowHeight((contentHeight - headerHeight) / rowsPerPage);
        }
    }, [tableContentRef.current, tableHeaderRef.current, loading]);
    const renderLabels = () => {
        return (_jsx(HeaderStyled, Object.assign({ ref: tableHeaderRef }, { children: labels.map((label) => {
                var _a;
                const { name, value, style = {} } = label || {};
                return (_jsx(HeaderDataContainerStyled, Object.assign({ width: (_a = rowItems[value]) === null || _a === void 0 ? void 0 : _a.width, style: Object.assign({}, style) }, { children: _jsx(Typography, Object.assign({ fontSize: 14, style: { lineHeight: 1.2 } }, { children: name })) }), value));
            }) })));
    };
    const renderTableBody = () => {
        const emptyRows = getEmptyRows();
        if (loadingData) {
            return [...Array(rowsPerPage)].map((_, index) => (_jsx(SkeletonRow, { "data-testid": `skeleton-row-${index}`, height: rowHeight, items: rowItems }, getUniqueKey())));
        }
        return (_jsxs("div", { children: [[...data].slice(...getSliceToShow()).map((rowItem) => {
                    const itemId = getUniqueKey();
                    return (_jsx(RowController, { data: rowItem, items: rowItems, notifyParent: notifyParent, rowControllerProps: rowControllerProps }, itemId));
                }), emptyRows > 0 && total !== 0 && (_jsx(EmptyTableRowStyled, Object.assign({ rowHeight: rowHeight, emptyRows: emptyRows }, { children: _jsx(Box, {}) })))] }));
    };
    return (_jsxs(TableContainerStyled, Object.assign({ ref: tableRef, style: Object.assign(Object.assign({}, (labels.filter(({ width }) => !width).length === 0 && !isMobile
            ? {
                width: `${labels.reduce((sum, { width }) => sum + (width || 0), 0)}px`,
            }
            : {})), tableStyleProp) }, rest, { children: [_jsxs(TableContentContainerStyled, Object.assign({ ref: tableContentRef }, { children: [_jsxs(TableDataContainerStyled, { children: [renderLabels(), ((data === null || data === void 0 ? void 0 : data.length) !== 0 || loadingData) && renderTableBody()] }), (data === null || data === void 0 ? void 0 : data.length) === 0 && !loadingData && (_jsx(EmptyTableRowsContainerStyled, Object.assign({ rowHeight: rowHeight, emptyRows: getEmptyRows() }, { children: _jsx(Box, { children: emptyLabel }) })))] })), rowsPerPage < total && (_jsx(TablePagination, { rowsPerPageOptions: rowsPerPageCurrentOptions, total: total, rowsPerPage: rowsPerPage, page: page, onPageChange: handlePageChange, onRowsPerPageChange: handleRowsPerPageChange, style: { padding: '20px 24px 19px 24px' }, loading: loadingData }))] })));
};
ComplexTable.defaultProps = {
    data: null,
    emptyLabel: (_jsxs(NoResultsFoundContainerStyled, { children: [_jsx(Typography, Object.assign({ variant: "h5", weight: "semi-bold" }, { children: "No encontramos resultados que coincidan con tu b\u00FAsqueda." })), _jsx(Typography, { children: "Prueba ajustando los filtros o ampliando los criterios" })] })),
    page: 0,
    total: 0,
    labels: [],
    rowsPerPage: 20,
    notifyParent: () => { },
    fetchOnPageChange: false,
    rowControllerProps: {},
    rowsPerPageOptions: [],
    loading: false,
};
export default ComplexTable;
//# sourceMappingURL=index.js.map